import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer, type Renderer } from '@googlemaps/markerclusterer';
import { GOOGLE_MAP_STYLE } from 'config/googleMapStyle';
import { GOOGLE_MAPS_API_KEY } from 'config/constants';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, type Store } from './storeData';

const createPinSvg = (fill: string): string =>
    `data:image/svg+xml,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="25" viewBox="0 0 20 25"><path d="M10 0C4.5 0 0 4.5 0 10c0 7.5 10 15 10 15s10-7.5 10-15C20 4.5 15.5 0 10 0z" fill="${fill}"/></svg>`,
    )}`;

const DEFAULT_PIN_SVG = createPinSvg('#e64b28');
const ACTIVE_PIN_SVG = createPinSvg('#171717');

const createClusterIcon = (count: number): string => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
    <circle cx="25" cy="25" r="25" fill="rgba(23,23,23,0.15)"/>
    <circle cx="25" cy="25" r="15" fill="#e64b28"/>
    <text x="25" y="25" text-anchor="middle" dominant-baseline="central"
          fill="#171717" font-family="'ABC Monument Grotesk Variable', sans-serif" font-size="12" font-weight="510" letter-spacing="-0.12">${count}</text>
  </svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const clusterRenderer: Renderer = {
    render: ({ count, position }) =>
        new google.maps.Marker({
            position,
            icon: {
                url: createClusterIcon(count),
                scaledSize: new google.maps.Size(50, 50),
                anchor: new google.maps.Point(25, 25),
            },
            label: { text: String(count), color: 'transparent' },
            zIndex: 1000 + count,
        }),
};

const containerStyle = {
    width: '100%',
    height: '100%',
};

interface StoreFinderMapProps {
    stores: Store[];
    isOpen?: boolean;
    selectedStore?: Store | null;
    onMarkerClick?: (store: Store) => void;
}

const StoreFinderMap = ({ stores, isOpen, selectedStore, onMarkerClick }: StoreFinderMapProps) => {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY || '',
    });
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const storeMarkerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
    const previousSelectedIdRef = useRef<string | null>(null);
    const iconsRef = useRef<{ default: google.maps.Icon; active: google.maps.Icon } | null>(null);
    const hasRequestedLocation = useRef(false);

    const mapOptions = useMemo(
        () => ({
            styles: GOOGLE_MAP_STYLE,
            disableDefaultUI: true,
            zoomControl: true,
        }),
        [],
    );

    const handleMapLoad = useCallback((mapInstance: google.maps.Map) => {
        iconsRef.current = {
            default: {
                url: DEFAULT_PIN_SVG,
                scaledSize: new google.maps.Size(20, 25),
                anchor: new google.maps.Point(10, 25),
            },
            active: {
                url: ACTIVE_PIN_SVG,
                scaledSize: new google.maps.Size(20, 25),
                anchor: new google.maps.Point(10, 25),
            },
        };
        setMap(mapInstance);
    }, []);

    // Reset geolocation flag when overlay closes
    useEffect(() => {
        if (!isOpen) {
            hasRequestedLocation.current = false;
        }
    }, [isOpen]);

    // Request geolocation only when the overlay is opened
    useEffect(() => {
        if (!isOpen || !map || hasRequestedLocation.current) return;
        hasRequestedLocation.current = true;

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    map.panTo(userLocation);
                    map.setZoom(10);
                },
                () => {
                    // Permission denied or error — keep default center
                },
            );
        }
    }, [isOpen, map]);

    // Create/update markers and clusterer when stores or map change
    useEffect(() => {
        if (!map || !iconsRef.current) return;

        // Clean up previous markers and clusterer
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }
        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];
        previousSelectedIdRef.current = null;

        // Create markers imperatively
        const newStoreMarkerMap = new Map<string, google.maps.Marker>();
        const newMarkers = stores
            .filter((store) => store.address.coordinates)
            .map((store) => {
                const marker = new google.maps.Marker({
                    position: {
                        lat: store.address.coordinates!.lat,
                        lng: store.address.coordinates!.lng,
                    },
                    icon: iconsRef.current!.default,
                    title: store.name,
                });

                marker.addListener('click', () => {
                    onMarkerClick?.(store);
                });

                newStoreMarkerMap.set(store._id, marker);

                return marker;
            });

        markersRef.current = newMarkers;
        storeMarkerMapRef.current = newStoreMarkerMap;

        // Create clusterer
        clustererRef.current = new MarkerClusterer({
            map,
            markers: newMarkers,
            renderer: clusterRenderer,
            onClusterClick: (_, cluster, clusterMap) => {
                const { bounds } = cluster;
                if (bounds) {
                    clusterMap.fitBounds(bounds);
                }
            },
        });

        return () => {
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
                clustererRef.current = null;
            }
            markersRef.current.forEach((marker) => marker.setMap(null));
            markersRef.current = [];
        };
    }, [map, stores, onMarkerClick]);

    // Highlight the selected store's marker
    useEffect(() => {
        if (!map || !iconsRef.current) return;

        const previousId = previousSelectedIdRef.current;
        const currentId = selectedStore?._id ?? null;

        if (previousId === currentId) return;

        // Reset previously selected marker to default
        if (previousId) {
            const previousMarker = storeMarkerMapRef.current.get(previousId);
            previousMarker?.setIcon(iconsRef.current.default);
        }

        // Set newly selected marker to active color
        if (currentId) {
            const activeMarker = storeMarkerMapRef.current.get(currentId);
            activeMarker?.setIcon(iconsRef.current.active);
        }

        previousSelectedIdRef.current = currentId;
    }, [map, selectedStore]);

    if (!isLoaded || !GOOGLE_MAPS_API_KEY) {
        return null;
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={DEFAULT_MAP_CENTER}
            zoom={DEFAULT_MAP_ZOOM}
            options={mapOptions}
            onLoad={handleMapLoad}
        />
    );
};

export default StoreFinderMap;
